import type { Keyset } from "./envelope";
import type { NotionBoardProperty, NotionBoardView, NotionCardProperties } from "./notion-properties";

/**
 * WSL Notion (apps/wsl-notion, migration 0043): pages, files and kanban
 * boards for SUPER_ADMIN logins. Wire shapes and limits only (snake_case);
 * the API's zod schemas and the database CHECKs are built from these.
 *
 * Who: the ACTIVE role SUPER_ADMIN, and nobody else (`NOTION_ROLES`). There is
 * no member list — holding the role is membership.
 */

// ── Who ─────────────────────────────────────────────────────────────────────

/** The roles that may use WSL Notion — read and write alike. */
export const NOTION_ROLES = ["SUPER_ADMIN"] as const;

// ── Pages ───────────────────────────────────────────────────────────────────

/** `notion_pages.kind` (`ck_notion_pages_kind`): a document, or a kanban board. */
export const NOTION_PAGE_KINDS = ["PAGE", "BOARD"] as const;
export type NotionPageKind = (typeof NOTION_PAGE_KINDS)[number];

export const NOTION_TITLE_MAX = 200;

// ── Icons ───────────────────────────────────────────────────────────────────

/**
 * A page icon is one string (`notion_pages.icon`, `ck_notion_pages_icon`), one of:
 *   an emoji                  "📘" (a flag or a ZWJ family can be several code points)
 *   a line icon in a colour   "icon:rocket:BLUE" — drawn by the app, never fetched
 *   a custom image            "custom:<uuid>"   — the workspace's icon library (0048)
 * The server checks the grammar (`isNotionIcon`); the app owns the names of
 * its line icons, so an unknown name just draws the default page icon.
 */
export const NOTION_ICON_MAX = 64;
export const NOTION_EMOJI_MAX = 16;
/** Colours a line icon may be drawn in. */
export const NOTION_ICON_COLORS = ["GRAY", "BROWN", "ORANGE", "YELLOW", "GREEN", "BLUE", "PURPLE", "PINK", "RED"] as const;
export type NotionIconColor = (typeof NOTION_ICON_COLORS)[number];

export type NotionIconValue =
	| { kind: "emoji"; emoji: string }
	| { kind: "icon"; name: string; color: NotionIconColor }
	| { kind: "custom"; id: string };

const LINE_ICON = /^icon:([a-z][a-z0-9-]{0,39}):([A-Z]+)$/;
const CUSTOM_ICON = /^custom:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/;

export const notionLineIcon = (name: string, color: NotionIconColor): string => `icon:${name}:${color}`;
export const notionCustomIcon = (id: string): string => `custom:${id}`;

/** Null = not a valid icon string (the API answers 422). */
export function parseNotionIcon(icon: string): NotionIconValue | null {
	if (icon.length === 0 || icon.length > NOTION_ICON_MAX) return null;
	const line = LINE_ICON.exec(icon);
	if (line) return (NOTION_ICON_COLORS as readonly string[]).includes(line[2]!) ? { kind: "icon", name: line[1]!, color: line[2] as NotionIconColor } : null;
	const custom = CUSTOM_ICON.exec(icon);
	if (custom) return { kind: "custom", id: custom[1]! };
	if (icon.startsWith("icon:") || icon.startsWith("custom:")) return null;
	return icon.length <= NOTION_EMOJI_MAX && !/[\s<>]/.test(icon) ? { kind: "emoji", emoji: icon } : null;
}

export const isNotionIcon = (icon: string): boolean => parseNotionIcon(icon) !== null;

/**
 * The workspace icon library (`notion_custom_icons`, 0048): images anyone in
 * the workspace uploaded to use as page icons. Bounded by a cap enforced on
 * insert, so `GET /notion/icons` returns it whole — the app needs every one
 * to draw any icon string, and reads it once.
 */
export const NOTION_CUSTOM_ICONS_MAX = 300;
export const NOTION_CUSTOM_ICON_NAME_MAX = 40;
export const NOTION_CUSTOM_ICON_MAX_BYTES = 5 * 1024 * 1024;
/** Long edge of a stored icon: it is drawn at most ~5rem. */
export const NOTION_CUSTOM_ICON_EDGE = 256;

export interface NotionCustomIcon {
	id: string;
	name: string;
	/** A cacheable short-lived URL (floored window), like a page's images. */
	url: string;
	created_at: string;
	created_by: NotionUserRef;
}
export interface NotionCustomIcons {
	items: NotionCustomIcon[];
}
/**
 * The serialised editor document. The API's body limit is 1 MB; images and
 * attachments are file ids, not bytes, so only a very long text gets near it.
 */
export const NOTION_DOC_MAX_BYTES = 900_000;
/** Plain text kept for search (`notion_page_contents.content_text`). */
export const NOTION_TEXT_MAX = 200_000;
/** How deep a document may nest (lists in lists in toggles…): a hostile body cannot blow the stack. */
export const NOTION_DOC_DEPTH_MAX = 40;
/** How deep pages may nest; also bounds the breadcrumb and cycle-check walks. */
export const NOTION_TREE_DEPTH_MAX = 20;
/** One page-history snapshot at most this often while someone types. */
export const NOTION_REVISION_INTERVAL_MINUTES = 10;
/** Search answers this many pages at most (title hits first). */
export const NOTION_SEARCH_LIMIT_MAX = 30;
export const NOTION_SEARCH_QUERY_MAX = 100;
export const NOTION_CURSOR_MAX = 300;

/** An empty editor document (Tiptap / ProseMirror JSON). */
export const NOTION_EMPTY_DOC: NotionDoc = { type: "doc", content: [] };

/** Tiptap / ProseMirror JSON. Kept loose on purpose: the editor owns its schema. */
export interface NotionDocNode {
	type: string;
	attrs?: Record<string, unknown>;
	content?: NotionDocNode[];
	text?: string;
	marks?: Array<{ type: string; attrs?: Record<string, unknown> }>;
}
export interface NotionDoc extends NotionDocNode {
	type: "doc";
}

/** Nodes after which the plain text breaks a line, so search never glues two paragraphs into one word. */
const BLOCK_BREAK = new Set([
	"paragraph",
	"heading",
	"listItem",
	"taskItem",
	"blockquote",
	"codeBlock",
	"callout",
	"tableCell",
	"tableHeader",
	"detailsSummary",
	"hardBreak",
]);

/**
 * The plain text of a document, for search: every text node in reading order,
 * a line break after each block, attachments by file name. Bounded by
 * `NOTION_DOC_DEPTH_MAX` and `NOTION_TEXT_MAX`, so a hostile body costs no
 * more than its size. The API runs it on every content save.
 */
export function notionDocText(doc: NotionDocNode): string {
	const out: string[] = [];
	let length = 0;
	const push = (s: string) => {
		if (length >= NOTION_TEXT_MAX || s === "") return;
		out.push(s);
		length += s.length;
	};
	const walk = (node: NotionDocNode, depth: number) => {
		if (depth > NOTION_DOC_DEPTH_MAX || length >= NOTION_TEXT_MAX) return;
		if (typeof node.text === "string") push(node.text);
		if (node.type === "attachment" && typeof node.attrs?.["file_name"] === "string") push(node.attrs["file_name"] as string);
		if (node.type === "pageMention" && typeof node.attrs?.["label"] === "string") push(`@${node.attrs["label"] as string}`);
		if (Array.isArray(node.content)) for (const child of node.content) if (child && typeof child === "object") walk(child, depth + 1);
		if (BLOCK_BREAK.has(node.type)) push("\n");
	};
	walk(doc, 0);
	return out
		.join("")
		.replace(/\n{3,}/g, "\n\n")
		.trim()
		.slice(0, NOTION_TEXT_MAX);
}

/** How deep a document nests (the root is 0). */
export function notionDocDepth(node: NotionDocNode, depth = 0): number {
	if (depth > NOTION_DOC_DEPTH_MAX || !Array.isArray(node.content)) return depth;
	let max = depth;
	for (const child of node.content) if (child && typeof child === "object") max = Math.max(max, notionDocDepth(child, depth + 1));
	return max;
}

/** The file ids a document points at (image and attachment blocks). */
export function notionDocFileIds(node: NotionDocNode, into: Set<string> = new Set(), depth = 0): Set<string> {
	if (depth > NOTION_DOC_DEPTH_MAX) return into;
	const id = node.attrs?.["file_id"];
	if ((node.type === "image" || node.type === "attachment") && typeof id === "string") into.add(id);
	if (Array.isArray(node.content)) for (const child of node.content) if (child && typeof child === "object") notionDocFileIds(child, into, depth + 1);
	return into;
}

/** The pages a document mentions (`@page` chips), at most NOTION_MENTIONS_MAX. */
export function notionDocPageIds(node: NotionDocNode, into: Set<string> = new Set(), depth = 0): Set<string> {
	if (depth > NOTION_DOC_DEPTH_MAX || into.size >= NOTION_MENTIONS_MAX) return into;
	const id = node.attrs?.["page_id"];
	if (node.type === "pageMention" && typeof id === "string" && /^[0-9a-f-]{36}$/i.test(id)) into.add(id);
	if (Array.isArray(node.content)) for (const child of node.content) if (child && typeof child === "object") notionDocPageIds(child, into, depth + 1);
	return into;
}

/** A page read resolves at most this many mentions (their current title, icon, whether still there). */
export const NOTION_MENTIONS_MAX = 200;

/** A mentioned page as it is NOW: chips show the live title, and say so when it is in the trash or gone. */
export interface NotionMention {
	id: string;
	kind: NotionPageKind;
	title: string;
	icon: string | null;
	archived: boolean;
}

export interface NotionUserRef {
	id: string;
	name: string;
}

/** A row of the sidebar tree (one level at a time). */
export interface NotionPageListItem {
	id: string;
	parent_id: string | null;
	kind: NotionPageKind;
	title: string;
	icon: string | null;
	has_children: boolean;
	updated_at: string;
}
export type NotionPageChildren = Keyset<NotionPageListItem>;

export interface NotionBreadcrumb {
	id: string;
	title: string;
	icon: string | null;
}

/** A file on a page. Images carry a short-lived URL; an attachment asks `GET /notion/files/:id/url` when clicked. */
export interface NotionFile {
	id: string;
	page_id: string;
	kind: NotionFileKind;
	file_name: string;
	content_type: string;
	size_bytes: number;
	width: number | null;
	height: number | null;
	url: string | null;
	created_at: string;
}

export interface NotionPageDetail {
	id: string;
	parent_id: string | null;
	kind: NotionPageKind;
	title: string;
	icon: string | null;
	/** Notion's "Full width": the body uses the window, not the reading measure. Shared by everyone. */
	full_width: boolean;
	/** The banner across the top (0049), or null. An image is one of this page's own files. */
	cover: NotionCover | null;
	/** Title / icon / place: PATCH and move carry it (→ STALE_VERSION). */
	version: number;
	/** Root first, the page itself excluded. Empty for a top-level page. */
	breadcrumbs: NotionBreadcrumb[];
	is_favorite: boolean;
	archived_at: string | null;
	created_at: string;
	created_by: NotionUserRef;
	updated_at: string;
	updated_by: NotionUserRef;
	content: {
		doc: NotionDoc;
		/** The body has its own version: a title edit and someone typing never refuse each other. */
		version: number;
		updated_at: string;
		updated_by: NotionUserRef;
	};
	/** Every file of this page (bounded by `NOTION_FILES_PER_PAGE_MAX`). */
	files: NotionFile[];
	/** The pages its body mentions, as they are now (absent = deleted for good). */
	mentions: NotionMention[];
}

export interface NotionContentSaved {
	version: number;
	updated_at: string;
	/** True when this save also wrote a page-history snapshot. */
	snapshot: boolean;
}

// ── Covers (0049) ───────────────────────────────────────────────────────────

/**
 * Ready-made covers, drawn by the app (CSS): colours and gradients, the
 * Games' ribbon among them. `notion_pages.cover_preset` (`ck_notion_pages_cover_preset`).
 */
export const NOTION_COVER_PRESETS = [
	"RIBBON",
	"BOARD",
	"SUNRISE",
	"OCEAN",
	"FOREST",
	"DUSK",
	"PEACH",
	"MINT",
	"GRAY",
	"RED",
	"ORANGE",
	"YELLOW",
	"GREEN",
	"BLUE",
	"PURPLE",
	"PINK",
] as const;
export type NotionCoverPreset = (typeof NOTION_COVER_PRESETS)[number];

/** Vertical focus of an image cover, 0 (top) … 100 (bottom). */
export const NOTION_COVER_POSITION_MAX = 100;

/**
 * A page cover: an image that is one of the page's own files (a composite FK
 * proves it, so a page can never show another page's file), or a preset.
 * Deleting the file clears the cover (ON DELETE SET NULL of that column).
 */
export type NotionCover = { kind: "image"; file_id: string; position: number } | { kind: "preset"; preset: NotionCoverPreset };

// ── Page history (snapshots) ────────────────────────────────────────────────

/** One page-history snapshot, as listed (newest first). */
export interface NotionRevisionItem {
	id: string;
	created_at: string;
	created_by: NotionUserRef;
	/** The body version it captured. */
	content_version: number;
}
export type NotionRevisionsPage = Keyset<NotionRevisionItem>;

export interface NotionRevisionDetail extends NotionRevisionItem {
	doc: NotionDoc;
}

/**
 * A restore: the snapshot becomes the body (a NEW body version, never a
 * rewind), and the body it replaced is snapshotted first, so a restore can
 * itself be undone from the history.
 */
export interface NotionRestored {
	doc: NotionDoc;
	version: number;
	updated_at: string;
}

/** A duplicate is the page alone (its sub-pages stay where they are), with copies of its files and its board. */
export const NOTION_DUPLICATE_SUFFIX = " (copy)";

export interface NotionSearchHit {
	id: string;
	kind: NotionPageKind;
	title: string;
	icon: string | null;
	/** A line of the body around the match, plain text; null for a title-only hit. */
	snippet: string | null;
	/** "A / B / C": where the page sits. */
	path: string;
	updated_at: string;
}

export interface NotionTrashItem {
	id: string;
	kind: NotionPageKind;
	title: string;
	icon: string | null;
	archived_at: string;
	archived_by: NotionUserRef;
	/** Pages archived with it (its sub-pages). */
	descendant_count: number;
}
export type NotionTrashPage = Keyset<NotionTrashItem>;

export interface NotionFavorite {
	id: string;
	kind: NotionPageKind;
	title: string;
	icon: string | null;
}
export type NotionFavoritesPage = Keyset<NotionFavorite>;

export interface NotionRecentItem {
	id: string;
	kind: NotionPageKind;
	title: string;
	icon: string | null;
	updated_at: string;
	updated_by: NotionUserRef;
}

// ── Files ───────────────────────────────────────────────────────────────────

/** `notion_files.kind`: an image block, or an attachment block. */
export const NOTION_FILE_KINDS = ["IMAGE", "ATTACHMENT"] as const;
export type NotionFileKind = (typeof NOTION_FILE_KINDS)[number];

/** Screenshots and photos. Re-encoded on upload (metadata stripped): PNG stays PNG, the rest become JPEG. */
export const NOTION_IMAGE_CONTENT_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;
/** What an image is stored as after re-encoding. */
export const NOTION_IMAGE_STORED_TYPES = ["image/png", "image/jpeg"] as const;

export const NOTION_DOCX = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
export const NOTION_XLSX = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
export const NOTION_PPTX = "application/vnd.openxmlformats-officedocument.presentationml.presentation";

/** Documents. Always served as a download, never rendered by the browser. */
export const NOTION_ATTACHMENT_CONTENT_TYPES = ["application/pdf", NOTION_DOCX, NOTION_XLSX, NOTION_PPTX, "text/csv", "text/plain"] as const;
export type NotionAttachmentContentType = (typeof NOTION_ATTACHMENT_CONTENT_TYPES)[number];

/** Everything `notion_files.content_type` may hold (`ck_notion_files_content_type`). */
/**
 * Any other file (.env, .zip, .json, .sql, …): accepted, stored as opaque
 * bytes and only ever served as a download — the browser never renders or
 * runs it. The types above keep their content checks (a ".pdf" that is not a
 * PDF is still refused).
 */
export const NOTION_GENERIC_CONTENT_TYPE = "application/octet-stream";

/**
 * Videos (0056): checked by their first bytes (MP4 / MOV: an `ftyp` box;
 * WebM: the EBML header) and kept as their type, so the app can play them
 * in a <video> element. Never served as a page, so they cannot run either.
 */
export const NOTION_VIDEO_CONTENT_TYPES = ["video/mp4", "video/webm", "video/quicktime"] as const;
export type NotionVideoContentType = (typeof NOTION_VIDEO_CONTENT_TYPES)[number];
export const isNotionVideoType = (contentType: string | null | undefined): contentType is NotionVideoContentType =>
	(NOTION_VIDEO_CONTENT_TYPES as readonly string[]).includes(contentType ?? "");
/**
 * A file stored before 0056 as opaque bytes whose NAME says video: the app
 * offers a player, and the server decides by the file's first bytes when it
 * is asked to play it (`GET /notion/files/:id/url?play=1`).
 */
export const NOTION_VIDEO_EXTENSIONS = [".mp4", ".m4v", ".mov", ".webm"] as const;
export const looksLikeNotionVideo = (contentType: string | null | undefined, fileName: string | null | undefined): boolean =>
	isNotionVideoType(contentType) ||
	(contentType === NOTION_GENERIC_CONTENT_TYPE && NOTION_VIDEO_EXTENSIONS.some((ext) => (fileName ?? "").toLowerCase().endsWith(ext)));

export const NOTION_STORED_CONTENT_TYPES = [...NOTION_IMAGE_STORED_TYPES, ...NOTION_ATTACHMENT_CONTENT_TYPES, ...NOTION_VIDEO_CONTENT_TYPES, NOTION_GENERIC_CONTENT_TYPE] as const;

/** Is this one of the document or video types whose first bytes are checked (and kept as its own type)? */
export const isKnownAttachmentType = (contentType: string): contentType is NotionAttachmentContentType | NotionVideoContentType =>
	(NOTION_ATTACHMENT_CONTENT_TYPES as readonly string[]).includes(contentType) || isNotionVideoType(contentType);

export const NOTION_IMAGE_MAX_BYTES = 15 * 1024 * 1024;
export const NOTION_ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;
export const NOTION_VIDEO_MAX_BYTES = 200 * 1024 * 1024;
/** The largest file of any kind (`ck_notion_files_size`). */
export const NOTION_FILE_MAX_BYTES = Math.max(NOTION_IMAGE_MAX_BYTES, NOTION_ATTACHMENT_MAX_BYTES, NOTION_VIDEO_MAX_BYTES);
/** Long edge of a stored image; larger ones are scaled down. */
export const NOTION_IMAGE_MAX_EDGE = 2400;
export const NOTION_FILE_NAME_MAX = 200;
/** A page holds at most this many files — the page read returns them all. */
export const NOTION_FILES_PER_PAGE_MAX = 200;

/** Which block a content type becomes, or null when it is not accepted. */
export function notionFileKindOf(contentType: string): NotionFileKind {
	return (NOTION_IMAGE_CONTENT_TYPES as readonly string[]).includes(contentType) ? "IMAGE" : "ATTACHMENT";
}

/** The upload limit for a file: a video (by its declared type) may be larger than other attachments. */
export const notionFileMaxBytes = (kind: NotionFileKind, contentType?: string): number =>
	kind === "IMAGE" ? NOTION_IMAGE_MAX_BYTES : isNotionVideoType(contentType) ? NOTION_VIDEO_MAX_BYTES : NOTION_ATTACHMENT_MAX_BYTES;

export interface NotionUploadStart {
	file_id: string;
	upload_url: string;
	upload_headers: Record<string, string>;
	upload_token: string;
	expires_at: string;
}

export interface NotionFileUrl {
	url: string;
	expires_at: string;
}

// ── Boards ──────────────────────────────────────────────────────────────────

/**
 * The named swatches of a column / a select option (0052 widened 8 → 18).
 * A colour is one of these OR a custom `#rrggbb` (lower-case) — see
 * `isNotionColor`; `ck_notion_board_columns_color` allows the same.
 */
export const NOTION_COLUMN_COLORS = [
	"GRAY",
	"BROWN",
	"ORANGE",
	"AMBER",
	"YELLOW",
	"LIME",
	"GREEN",
	"TEAL",
	"CYAN",
	"SKY",
	"BLUE",
	"INDIGO",
	"VIOLET",
	"PURPLE",
	"PINK",
	"ROSE",
	"RED",
	"SLATE",
] as const;
export type NotionNamedColor = (typeof NOTION_COLUMN_COLORS)[number];
/** A named swatch, or a custom colour `#rrggbb`. */
export type NotionColumnColor = NotionNamedColor | `#${string}`;
/** A custom colour as stored: `#` and six lower-case hex digits. Mirrored by the DB CHECK. */
export const NOTION_CUSTOM_COLOR_PATTERN = "^#[0-9a-f]{6}$";
const CUSTOM_COLOR = new RegExp(NOTION_CUSTOM_COLOR_PATTERN);
export const isNotionColor = (v: unknown): v is NotionColumnColor =>
	typeof v === "string" && ((NOTION_COLUMN_COLORS as readonly string[]).includes(v) || CUSTOM_COLOR.test(v));
export const isCustomColor = (v: string): v is `#${string}` => CUSTOM_COLOR.test(v);

/** Each named swatch's base colour; chips and dots are drawn from it (a custom colour is its own base). */
export const NOTION_COLOR_HEX: Record<NotionNamedColor, string> = {
	GRAY: "#9b9a97",
	BROWN: "#a0705a",
	ORANGE: "#e8590c",
	AMBER: "#f59f00",
	YELLOW: "#d9a400",
	LIME: "#74b816",
	GREEN: "#0e7a4b",
	TEAL: "#0ca678",
	CYAN: "#1098ad",
	SKY: "#3bb3f0",
	BLUE: "#2f80ed",
	INDIGO: "#4c6ef5",
	VIOLET: "#7950f2",
	PURPLE: "#9c36b5",
	PINK: "#db2777",
	ROSE: "#f06595",
	RED: "#d92d20",
	SLATE: "#495057",
};
export const notionColorHex = (c: NotionColumnColor): string => (isCustomColor(c) ? c : (NOTION_COLOR_HEX[c as NotionNamedColor] ?? NOTION_COLOR_HEX.GRAY));

/** A new board starts with these. */
export const NOTION_BOARD_DEFAULT_COLUMNS: ReadonlyArray<{ name: string; color: NotionColumnColor }> = [
	{ name: "To do", color: "GRAY" },
	{ name: "In progress", color: "BLUE" },
	{ name: "Done", color: "GREEN" },
];

export const NOTION_COLUMN_NAME_MAX = 60;
export const NOTION_COLUMNS_MAX = 20;
export const NOTION_CARD_TITLE_MAX = 200;
export const NOTION_CARD_LABELS_MAX = 8;
export const NOTION_CARD_LABEL_MAX = 30;
/** A card's assignees (0054), in the order they were picked. */
export const NOTION_CARD_ASSIGNEES_MAX = 10;
/** Cards per column in the board read; more come from the column's own keyset. */
export const NOTION_BOARD_CARDS_PAGE = 50;

export interface NotionCardListItem {
	id: string;
	column_id: string;
	title: string;
	/** Everyone assigned (0054), in the order they were picked. */
	assignees: NotionUserRef[];
	/** @deprecated The first of `assignees` — kept for a web app older than 0054. */
	assignee: NotionUserRef | null;
	/** `YYYY-MM-DD`, a calendar day. */
	due_date: string | null;
	labels: string[];
	has_body: boolean;
	/** Its values for the board's own properties (0050), by property id; absent = empty. */
	properties: NotionCardProperties;
	created_at: string;
	updated_at: string;
	version: number;
}

export interface NotionBoardColumn {
	id: string;
	name: string;
	color: NotionColumnColor;
	version: number;
	card_count: number;
	cards: NotionCardListItem[];
	next_cursor: string | null;
}

/**
 * A column of a grouped board (`group` on the read): the cards holding one
 * person / option (`key`), or none (`NOTION_GROUP_NONE`, `name` null). A card
 * with several values shows in each of their groups.
 */
export interface NotionBoardGroup {
	key: string;
	/** The person's / option's name; null for the "no value" group (the app names it). */
	name: string | null;
	/** The option's colour; null for people and for "no value". */
	color: NotionColumnColor | null;
	card_count: number;
	cards: NotionCardListItem[];
	next_cursor: string | null;
}

export interface NotionBoard {
	page_id: string;
	/** The page's version: a view write carries it (→ STALE_VERSION). */
	page_version: number;
	/** The status columns. On a grouped read they carry their counts but no cards (the cards are in `groups`). */
	columns: NotionBoardColumn[];
	/** The columns by the read's `group`, in order with "no value" last; null when the board is read by status. */
	groups: NotionBoardGroup[] | null;
	/** The board's own fields (0050), in order. */
	properties: NotionBoardProperty[];
	/** The ONE shared view: filters, sort, card face. Columns and counts are AFTER its filters (and the search). */
	view: NotionBoardView;
}

export type NotionColumnCards = Keyset<NotionCardListItem>;

export interface NotionCardDetail extends NotionCardListItem {
	page_id: string;
	doc: NotionDoc | null;
	created_at: string;
	created_by: NotionUserRef;
	updated_at: string;
	updated_by: NotionUserRef;
}

/** A SUPER_ADMIN login, for the assignee picker. */
export interface NotionMember {
	id: string;
	name: string;
	username: string;
	email: string | null;
	last_login_at: string | null;
}
export type NotionMembersPage = Keyset<NotionMember>;

// ── Card comments (0055) ────────────────────────────────────────────────────

/** Plain text (line breaks kept, links drawn by the app), trimmed, 1..MAX characters. */
export const NOTION_CARD_COMMENT_MAX = 4000;
/** One page of a card's comments, newest first on the wire (the app shows them oldest at the top). */
export const NOTION_CARD_COMMENTS_PAGE = 20;

export interface NotionCardComment {
	id: string;
	card_id: string;
	body: string;
	/** Null once the author's login was deleted. */
	author: NotionUserRef | null;
	created_at: string;
	/** Null = never edited. */
	edited_at: string | null;
}
export interface NotionCardCommentsPage extends Keyset<NotionCardComment> {
	/** Every comment on the card, on the first page only; null on later pages. */
	total: number | null;
}

// ── Notifications (0051) ────────────────────────────────────────────────────

/**
 * Why someone was told: made a card's assignee, set in one of its PERSON
 * properties (`property_name` names it), or a comment on a card they are
 * assigned to or commented on (CARD_COMMENT, 0055). Written by the write
 * itself, never for a change you made to yourself.
 */
export const NOTION_NOTIFICATION_KINDS = ["CARD_ASSIGNED", "CARD_PERSON", "CARD_COMMENT"] as const;
export type NotionNotificationKind = (typeof NOTION_NOTIFICATION_KINDS)[number];

/** The bell's page size, and how many ids one "mark read" may name. */
export const NOTION_NOTIFICATIONS_PAGE_MAX = 50;
export const NOTION_NOTIFICATIONS_READ_MAX = 100;
/** The unread badge counts this far, then reads "99+": the count never walks a whole inbox. */
export const NOTION_NOTIFICATIONS_UNREAD_CAP = 100;
/** How often an open app asks for its unread count (and again whenever the tab comes back). */
export const NOTION_NOTIFICATIONS_POLL_SECONDS = 30;

export interface NotionNotification {
	id: string;
	kind: NotionNotificationKind;
	created_at: string;
	/** Null = unread. */
	read_at: string | null;
	/** Who made the change. */
	actor: NotionUserRef;
	/** The PERSON property, for CARD_PERSON (its name when it was set). */
	property_name: string | null;
	board: { id: string; title: string; icon: string | null; archived: boolean };
	card: { id: string; title: string };
}
export interface NotionNotificationsPage extends Keyset<NotionNotification> {
	/** On the first page only (capped at NOTION_NOTIFICATIONS_UNREAD_CAP); null on later pages. */
	unread: number | null;
}

// ── Presence ────────────────────────────────────────────────────────────────

/**
 * Who has a page or a card open right now. Redis only, never the database:
 * an open, visible tab says "I am here" every HEARTBEAT seconds and is
 * forgotten TTL seconds after its last word (a closed laptop drops off on
 * its own). Decoration, never a gate — Redis down = nobody shown.
 */
export const NOTION_PRESENCE_TARGETS = ["page", "card"] as const;
export type NotionPresenceTarget = (typeof NOTION_PRESENCE_TARGETS)[number];
export const NOTION_PRESENCE_HEARTBEAT_SECONDS = 15;
export const NOTION_PRESENCE_TTL_SECONDS = 45;

export interface NotionPresence {
	/** Everyone there, the caller included, most recently seen first. Names come from the members list. */
	viewers: Array<{ user_id: string; seen_at: string }>;
	/** False when Redis did not answer: the list is empty, not "nobody". */
	available: boolean;
}

// ── Activity ────────────────────────────────────────────────────────────────

/**
 * What an activity row is about: the page itself (created, renamed, moved,
 * trashed, restored, deleted), its body (a history snapshot = someone edited
 * the text), a file, a board column or a card.
 */
export const NOTION_ACTIVITY_SUBJECTS = ["page", "body", "file", "column", "card"] as const;
export type NotionActivitySubject = (typeof NOTION_ACTIVITY_SUBJECTS)[number];

/** INSERT / UPDATE / DELETE come from the audit log; EDIT is a body snapshot. */
export type NotionActivityAction = "INSERT" | "UPDATE" | "DELETE" | "EDIT";

export interface NotionActivityItem {
	id: string;
	at: string;
	subject: NotionActivitySubject;
	action: NotionActivityAction;
	/** Null for a system / migration write. */
	actor: NotionUserRef | null;
	actor_type: string;
	/** The audit headline ("Renamed page to …"); null for a body edit. */
	reason: string | null;
	changed_fields: string[];
	/** The file / card / column it names, or the page title at the time. */
	target: string | null;
	page: { id: string; title: string; icon: string | null; kind: NotionPageKind | null; exists: boolean };
}
export type NotionActivityPage = Keyset<NotionActivityItem>;

// ── Ordering keys ───────────────────────────────────────────────────────────

/**
 * Fractional ordering keys (David Greenspan's scheme, as in rocicorp's
 * `fractional-indexing`): a key strictly between any two others always
 * exists, so moving a page or a card writes ONE row and renumbers nothing.
 * Keys compare as plain bytes — the columns are `COLLATE "C"`, and JS string
 * comparison is the same order for these characters.
 */
const DIGITS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
const ZERO = DIGITS[0]!;
const LAST = DIGITS[DIGITS.length - 1]!;
const SMALLEST_INTEGER = `A${ZERO.repeat(26)}`;

function midpoint(a: string, b: string | null): string {
	if (b !== null && a >= b) throw new Error(`order key: ${a} >= ${b}`);
	if (a.slice(-1) === ZERO || (b !== null && b.slice(-1) === ZERO)) throw new Error("order key: trailing zero");
	if (b !== null) {
		let n = 0;
		while ((a[n] ?? ZERO) === b[n]) n++;
		if (n > 0) return b.slice(0, n) + midpoint(a.slice(n), b.slice(n));
	}
	const digitA = a ? DIGITS.indexOf(a[0]!) : 0;
	const digitB = b !== null ? DIGITS.indexOf(b[0]!) : DIGITS.length;
	if (digitB - digitA > 1) return DIGITS[Math.round(0.5 * (digitA + digitB))]!;
	if (b !== null && b.length > 1) return b.slice(0, 1);
	return DIGITS[digitA]! + midpoint(a.slice(1), null);
}

function integerLength(head: string): number {
	if (head >= "a" && head <= "z") return head.charCodeAt(0) - 97 + 2;
	if (head >= "A" && head <= "Z") return 90 - head.charCodeAt(0) + 2;
	throw new Error(`order key: bad head ${head}`);
}

function integerPart(key: string): string {
	const len = integerLength(key[0] ?? "");
	if (len > key.length) throw new Error(`order key: too short ${key}`);
	return key.slice(0, len);
}

function incrementInteger(x: string): string | null {
	const head = x[0]!;
	const digits = x.slice(1).split("");
	let carry = true;
	for (let i = digits.length - 1; carry && i >= 0; i--) {
		const d = DIGITS.indexOf(digits[i]!) + 1;
		if (d === DIGITS.length) digits[i] = ZERO;
		else {
			digits[i] = DIGITS[d]!;
			carry = false;
		}
	}
	if (!carry) return head + digits.join("");
	if (head === "Z") return `a${ZERO}`;
	if (head === "z") return null;
	const next = String.fromCharCode(head.charCodeAt(0) + 1);
	if (next > "a") digits.push(ZERO);
	else digits.pop();
	return next + digits.join("");
}

function decrementInteger(x: string): string | null {
	const head = x[0]!;
	const digits = x.slice(1).split("");
	let borrow = true;
	for (let i = digits.length - 1; borrow && i >= 0; i--) {
		const d = DIGITS.indexOf(digits[i]!) - 1;
		if (d === -1) digits[i] = LAST;
		else {
			digits[i] = DIGITS[d]!;
			borrow = false;
		}
	}
	if (!borrow) return head + digits.join("");
	if (head === "a") return `Z${LAST}`;
	if (head === "A") return null;
	const next = String.fromCharCode(head.charCodeAt(0) - 1);
	if (next < "Z") digits.push(LAST);
	else digits.pop();
	return next + digits.join("");
}

/** Is this a well-formed ordering key? */
export function isOrderKey(key: string): boolean {
	try {
		if (key === SMALLEST_INTEGER) return false;
		const i = integerPart(key);
		if ([...key].some((ch) => !DIGITS.includes(ch))) return false;
		return key.slice(i.length).slice(-1) !== ZERO;
	} catch {
		return false;
	}
}

/**
 * A key strictly between `a` and `b` (null = open end). `between(null, null)`
 * is the first key; `between(last, null)` appends; `between(null, first)`
 * prepends. Throws on `a >= b` or a malformed key — the caller read them from
 * the database, so that is a bug, not input.
 */
export function orderKeyBetween(a: string | null, b: string | null): string {
	if (a !== null && !isOrderKey(a)) throw new Error(`order key: invalid ${a}`);
	if (b !== null && !isOrderKey(b)) throw new Error(`order key: invalid ${b}`);
	if (a !== null && b !== null && a >= b) throw new Error(`order key: ${a} >= ${b}`);
	if (a === null) {
		if (b === null) return `a${ZERO}`;
		const ib = integerPart(b);
		const fb = b.slice(ib.length);
		if (ib === SMALLEST_INTEGER) return ib + midpoint("", fb);
		if (ib < b) return ib;
		const dec = decrementInteger(ib);
		if (dec === null) throw new Error("order key: cannot go lower");
		return dec;
	}
	if (b === null) {
		const ia = integerPart(a);
		const fa = a.slice(ia.length);
		const inc = incrementInteger(ia);
		return inc === null ? ia + midpoint(fa, null) : inc;
	}
	const ia = integerPart(a);
	const fa = a.slice(ia.length);
	const ib = integerPart(b);
	const fb = b.slice(ib.length);
	if (ia === ib) return ia + midpoint(fa, fb);
	const inc = incrementInteger(ia);
	if (inc === null) throw new Error("order key: cannot go higher");
	return inc < b ? inc : ia + midpoint(fa, null);
}

/** `n` keys in order after `a` (null = from the start): a new board's columns. */
export function orderKeysAfter(a: string | null, n: number): string[] {
	const keys: string[] = [];
	let prev = a;
	for (let i = 0; i < n; i++) {
		prev = orderKeyBetween(prev, null);
		keys.push(prev);
	}
	return keys;
}

/** Keys grow by about one character per insert at the same spot; the CHECK only stops a runaway. */
export const NOTION_ORDER_KEY_MAX = 200;
